'use client'

import { ClientSelector } from '@/components/client/ClientSelector'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { Client } from '@/lib/api/clients/client-api'
import { Partner } from '@/lib/api/common/interfaces/partner.interface'
import {
    Consultation,
    getConsultationsByCompany,
} from '@/lib/api/consultations/consultation-api'
import { Exam, getExamsByCompany } from '@/lib/api/exams/exam-api'
import {
    createNfse,
    CreateNfseData,
    FiscalReadiness,
    getFiscalReadiness,
} from '@/lib/api/fiscal/fiscal-api'
import {
    Order,
    getOrdersByCompany,
} from '@/lib/api/orders/order-api'
import { SERVICE_LIST_ITEMS } from '@/lib/constants/service-list-items'
import { SearchableSelect } from '@/components/forms/searchable-select'
import { fetchCnpj } from '@/lib/api/fetch-cnpj'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useCallback, useEffect, useRef, useState } from 'react'
import { MdArrowBack, MdClose, MdSearch, MdWarning } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

type TomadorSource = 'client' | 'partner' | 'manual'
type ServiceSource = 'consultation' | 'exam' | 'order' | 'manual'

interface ServiceResult {
    _type: ServiceSource
    id: number
    [key: string]: any
}

const NfseFormPage = () => {
    const { user } = useAuth()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [readiness, setReadiness] = useState<FiscalReadiness | null>(null)
    const [readinessLoading, setReadinessLoading] = useState(true)
    const segment = user?.company?.segment
    const isHealth = segment === 'HEALTH'

    useEffect(() => {
        const checkReadiness = async () => {
            const res = await getFiscalReadiness()
            if (res.success) setReadiness(res.result || null)
            setReadinessLoading(false)
        }
        checkReadiness()
    }, [])

    // Tomador
    const [tomadorSource, setTomadorSource] = useState<TomadorSource>('manual')
    const [selectedClientId, setSelectedClientId] = useState<number | null>(
        null
    )
    const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(
        null
    )

    // Serviço
    const [serviceSource, setServiceSource] = useState<ServiceSource>('manual')
    const [serviceSearchTerm, setServiceSearchTerm] = useState('')
    const [serviceResults, setServiceResults] = useState<ServiceResult[]>([])
    const [serviceLoading, setServiceLoading] = useState(false)
    const [showServiceDropdown, setShowServiceDropdown] = useState(false)
    const [selectedService, setSelectedService] =
        useState<ServiceResult | null>(null)
    const serviceContainerRef = useRef<HTMLDivElement>(null)
    const serviceDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
        null
    )

    // Form
    const [formData, setFormData] = useState<CreateNfseData>({
        valorServicos: 0,
        discriminacao: '',
        issRetido: false,
        itemListaServico: '',
        tomadorCnpjCpf: '',
        tomadorRazaoSocial: '',
    })

    // Pré-preencher item lista serviço da empresa quando readiness carregar
    useEffect(() => {
        if (readiness?.serviceListItem) {
            setFormData(prev => ({
                ...prev,
                itemListaServico:
                    prev.itemListaServico || readiness.serviceListItem!,
            }))
        }
    }, [readiness])
    const [valorDisplay, setValorDisplay] = useState('')

    const update = (field: keyof CreateNfseData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const formatCurrency = (cents: number) =>
        new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(cents / 100)

    const handleValorChange = (value: string) => {
        const numbers = value.replace(/\D/g, '')
        const cents = parseInt(numbers) || 0
        setFormData(prev => ({ ...prev, valorServicos: cents }))
        setValorDisplay(formatCurrency(cents))
    }

    // === Tomador handlers ===

    const handleTomadorSourceChange = (source: TomadorSource) => {
        setTomadorSource(source)
        setSelectedClientId(null)
        setSelectedPartnerId(null)
        setFormData(prev => ({
            ...prev,
            clientId: undefined,
            tomadorCnpjCpf: '',
            tomadorRazaoSocial: '',
        }))
    }

    const handleClientSelect = (_id: number | null, client: Client | null) => {
        setSelectedClientId(client?.id || null)
        if (client) {
            setFormData(prev => ({
                ...prev,
                clientId: client.id,
                tomadorCnpjCpf: client.cpf || '',
                tomadorRazaoSocial: client.name,
            }))
        }
    }

    const handlePartnerSelect = (
        _id: number | null,
        partner: Partner | null
    ) => {
        setSelectedPartnerId(partner?.id || null)
        if (partner) {
            setFormData(prev => ({
                ...prev,
                tomadorCnpjCpf: partner.cnpj || partner.cpf || '',
                tomadorRazaoSocial: partner.name,
            }))
        }
    }

    const [cnpjLoading, setCnpjLoading] = useState(false)

    const handleCnpjBlur = async () => {
        if (tomadorSource !== 'manual') return
        const clean = formData.tomadorCnpjCpf.replace(/\D/g, '')
        if (clean.length !== 14) return
        setCnpjLoading(true)
        const data = await fetchCnpj(clean)
        if (data) {
            setFormData(prev => ({
                ...prev,
                tomadorRazaoSocial: prev.tomadorRazaoSocial || data.razaoSocial,
            }))
        }
        setCnpjLoading(false)
    }

    // === Service search ===

    const serviceSourceOptions: { value: ServiceSource; label: string }[] =
        isHealth
            ? [
                  { value: 'consultation', label: 'Consulta' },
                  { value: 'exam', label: 'Exame' },
                  { value: 'order', label: 'Procedimento' },
                  { value: 'manual', label: 'Preencher manualmente' },
              ]
            : [{ value: 'manual', label: 'Preencher manualmente' }]

    const searchServices = useCallback(
        async (term: string) => {
            if (!user?.companyId || serviceSource === 'manual') return
            setServiceLoading(true)
            try {
                let results: ServiceResult[] = []
                if (serviceSource === 'consultation') {
                    const r = await getConsultationsByCompany(user.companyId, {
                        search: term,
                        limit: 20,
                    })
                    if (r.success)
                        results = (r.result || []).map(c => ({
                            ...c,
                            _type: 'consultation' as const,
                        }))
                } else if (serviceSource === 'exam') {
                    const r = await getExamsByCompany(user.companyId, {
                        search: term,
                        limit: 20,
                    })
                    if (r.success)
                        results = (r.result || []).map(e => ({
                            ...e,
                            _type: 'exam' as const,
                        }))
                } else if (serviceSource === 'order') {
                    const r = await getOrdersByCompany(user.companyId, {
                        search: term,
                        limit: 20,
                    })
                    if (r.success)
                        results = (r.result || []).map(p => ({
                            ...p,
                            _type: 'order' as const,
                        }))
                }
                setServiceResults(results)
            } catch {
                // silent
            } finally {
                setServiceLoading(false)
            }
        },
        [user?.companyId, serviceSource]
    )

    useEffect(() => {
        if (!showServiceDropdown || serviceSource === 'manual') return
        if (serviceDebounceRef.current) clearTimeout(serviceDebounceRef.current)
        serviceDebounceRef.current = setTimeout(() => {
            searchServices(serviceSearchTerm)
        }, 300)
        return () => {
            if (serviceDebounceRef.current)
                clearTimeout(serviceDebounceRef.current)
        }
    }, [serviceSearchTerm, showServiceDropdown, searchServices])

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (
                serviceContainerRef.current &&
                !serviceContainerRef.current.contains(e.target as Node)
            ) {
                setShowServiceDropdown(false)
            }
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const handleServiceSourceChange = (source: ServiceSource) => {
        setServiceSource(source)
        setSelectedService(null)
        setServiceSearchTerm('')
        setServiceResults([])
        setShowServiceDropdown(false)
    }

    const handleServiceSelect = (item: ServiceResult) => {
        setSelectedService(item)
        setShowServiceDropdown(false)
        setServiceSearchTerm('')

        if (item._type === 'consultation') {
            const c = item as unknown as Consultation
            const catalog = c.catalog
            const specialtyNames = catalog?.specialties?.length
                ? catalog.specialties
                      .map((cs: any) => cs.specialty.name)
                      .join(', ')
                : catalog?.specialty?.name
            const desc =
                c.title ||
                (specialtyNames
                    ? `Consulta - ${specialtyNames}`
                    : catalog?.name || 'Consulta médica')
            const catalogPrice = catalog?.sellingPrice || catalog?.price || 0
            const discount = c.discount || 0
            // Arredonda o DESCONTO, não o resultado — idêntico a `resolvePricing` no
            // backend (`base - Math.round((base * d) / 100)`), que é quem apura o
            // valor do atendimento. Arredondando o resultado, meio centavo caía para
            // o outro lado (3% de R$ 39,50 dava R$ 38,32 aqui e R$ 38,31 na cobrança)
            // — e aqui a diferença ia para a NOTA FISCAL, divergindo do que o cliente
            // pagou.
            const valor =
                discount > 0
                    ? catalogPrice - Math.round((catalogPrice * discount) / 100)
                    : catalogPrice
            setFormData(prev => ({
                ...prev,
                serviceType: 'consultation',
                serviceId: c.id,
                discriminacao: desc,
                valorServicos: valor,
            }))
            setValorDisplay(formatCurrency(valor))
        } else if (item._type === 'exam') {
            const e = item as unknown as Exam
            const desc =
                e.title ||
                e.items
                    ?.map(i => i.catalog?.name)
                    .filter(Boolean)
                    .join(', ') ||
                'Exame'
            const valor = e.finalPrice || e.totalPrice || 0
            setFormData(prev => ({
                ...prev,
                serviceType: 'exam',
                serviceId: e.id,
                discriminacao: desc,
                valorServicos: valor,
            }))
            setValorDisplay(formatCurrency(valor))
        } else if (item._type === 'order') {
            const p = item as unknown as Order
            const desc = p.catalog?.name || p.description || 'Procedimento'
            const valor = p.catalog?.sellingPrice || p.catalog?.price || 0
            setFormData(prev => ({
                ...prev,
                serviceType: 'order',
                serviceId: p.id,
                discriminacao: desc,
                valorServicos: valor,
            }))
            setValorDisplay(formatCurrency(valor))
        }
    }

    const clearSelectedService = () => {
        setSelectedService(null)
        setFormData(prev => ({
            ...prev,
            serviceType: undefined,
            serviceId: undefined,
        }))
    }

    const getServiceResultLabel = (
        item: ServiceResult
    ): { title: string; subtitle: string } => {
        if (item._type === 'consultation') {
            const c = item as unknown as Consultation
            const date = c.scheduledAt
                ? format(new Date(c.scheduledAt), 'dd/MM/yyyy', {
                      locale: ptBR,
                  })
                : ''
            return {
                title: `#${c.id} - ${c.client?.name || 'Sem cliente'}`,
                subtitle: `${c.catalog?.specialty?.name || c.title || c.catalog?.name || 'Consulta'}${date ? ` - ${date}` : ''}${c.catalog?.sellingPrice ? ` - ${formatCurrency(c.catalog.sellingPrice)}` : ''}`,
            }
        }
        if (item._type === 'exam') {
            const e = item as unknown as Exam
            return {
                title: `#${e.id} - ${e.client?.name || 'Sem cliente'}`,
                subtitle: `${e.title || 'Exame'}${e.finalPrice ? ` - ${formatCurrency(e.finalPrice)}` : ''}`,
            }
        }
        if (item._type === 'order') {
            const p = item as unknown as Order
            const date = p.scheduledAt
                ? format(new Date(p.scheduledAt), 'dd/MM/yyyy', {
                      locale: ptBR,
                  })
                : ''
            return {
                title: `#${p.id} - ${p.client?.name || 'Sem cliente'}`,
                subtitle: `${p.catalog?.name || 'Procedimento'}${date ? ` - ${date}` : ''}`,
            }
        }
        return { title: '', subtitle: '' }
    }

    const getSelectedServiceLabel = (): string => {
        if (!selectedService) return ''
        const { _type } = selectedService
        const typeLabels: Record<string, string> = {
            consultation: 'Consulta',
            exam: 'Exame',
            order: 'Procedimento',
        }
        const label = typeLabels[_type] || ''
        const data = selectedService as any
        return `${label} #${data.id} - ${data.client?.name || ''}`
    }

    // === Submit ===

    const handleSubmit = async () => {
        if (!formData.tomadorCnpjCpf || !formData.tomadorRazaoSocial) {
            toast.error('Preencha os dados do tomador')
            return
        }
        if (!formData.valorServicos) {
            toast.error('Informe o valor do serviço')
            return
        }
        if (!formData.discriminacao) {
            toast.error('Informe a discriminação do serviço')
            return
        }
        if (!formData.itemListaServico) {
            toast.error('Informe o item da lista de serviço')
            return
        }

        setLoading(true)
        const response = await createNfse(formData)
        if (response.success) {
            toast.success('NFS-e enviada para processamento')
            navigate('/fiscal/invoices')
        } else {
            toast.error(response.error)
        }
        setLoading(false)
    }

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
                <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => navigate('/fiscal/invoices')}
                >
                    <MdArrowBack size={20} />
                </button>
                <h1 className="text-2xl font-bold">Emitir NFS-e</h1>
            </div>

            {readinessLoading && (
                <div className="flex justify-center py-12">
                    <span className="loading loading-spinner loading-lg"></span>
                </div>
            )}

            {!readinessLoading && readiness && !readiness.nfse.ready && (
                <div className="alert alert-warning mb-6">
                    <MdWarning size={24} />
                    <div>
                        <h3 className="font-bold">
                            Configuração incompleta para emissão de NFS-e
                        </h3>
                        <ul className="list-disc list-inside mt-1 text-sm">
                            {readiness.nfse.missingFields.map(field => (
                                <li key={field}>{field}</li>
                            ))}
                        </ul>
                        <div className="flex gap-2 mt-3">
                            {!readiness.isProvisioned && (
                                <button
                                    className="btn btn-sm btn-outline"
                                    onClick={() => navigate('/fiscal/settings')}
                                >
                                    Configurações fiscais
                                </button>
                            )}
                            <button
                                className="btn btn-sm btn-outline"
                                onClick={() => navigate('/conta?tab=company')}
                            >
                                Dados da empresa
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {!readinessLoading && readiness?.nfse.ready && (
                <>
                    {/* Tomador */}
                    <div className="card bg-base-100 shadow-sm border border-base-200 mb-6">
                        <div className="card-body">
                            <h2 className="card-title text-lg mb-4">
                                Tomador do Serviço
                            </h2>

                            <div className="flex gap-2 mb-4">
                                {(
                                    [
                                        'client',
                                        'partner',
                                        'manual',
                                    ] as TomadorSource[]
                                ).map(source => (
                                    <button
                                        key={source}
                                        className={`btn btn-sm ${tomadorSource === source ? 'btn-secondary' : 'btn-ghost'}`}
                                        onClick={() =>
                                            handleTomadorSourceChange(source)
                                        }
                                    >
                                        {source === 'client'
                                            ? 'Cliente'
                                            : source === 'partner'
                                              ? 'Parceiro'
                                              : 'Manual'}
                                    </button>
                                ))}
                            </div>

                            {tomadorSource === 'client' && (
                                <ClientSelector
                                    value={selectedClientId}
                                    onChange={handleClientSelect}
                                    label=""
                                    placeholder="Buscar cliente..."
                                    className="mb-4"
                                />
                            )}

                            {tomadorSource === 'partner' && (
                                <PartnerSelector
                                    value={selectedPartnerId}
                                    onChange={handlePartnerSelect}
                                    label=""
                                    placeholder="Buscar parceiro..."
                                    className="mb-4"
                                />
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">
                                            CNPJ/CPF *
                                        </span>
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            className="input input-bordered w-full"
                                            value={formData.tomadorCnpjCpf}
                                            onChange={e =>
                                                update(
                                                    'tomadorCnpjCpf',
                                                    e.target.value
                                                )
                                            }
                                            onBlur={handleCnpjBlur}
                                            placeholder="00.000.000/0000-00"
                                            readOnly={
                                                tomadorSource !== 'manual'
                                            }
                                        />
                                        {cnpjLoading && (
                                            <span className="loading loading-spinner loading-xs absolute right-3 top-1/2 -translate-y-1/2"></span>
                                        )}
                                    </div>
                                </div>
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">
                                            Razão Social *
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered"
                                        value={formData.tomadorRazaoSocial}
                                        onChange={e =>
                                            update(
                                                'tomadorRazaoSocial',
                                                e.target.value
                                            )
                                        }
                                        readOnly={tomadorSource !== 'manual'}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Servico */}
                    <div className="card bg-base-100 shadow-sm border border-base-200 mb-6">
                        <div className="card-body">
                            <h2 className="card-title text-lg mb-4">Serviço</h2>

                            <div className="form-control mb-4">
                                <label className="label">
                                    <span className="label-text">
                                        Referência
                                    </span>
                                </label>
                                <select
                                    className="select select-bordered"
                                    value={serviceSource}
                                    onChange={e =>
                                        handleServiceSourceChange(
                                            e.target.value as ServiceSource
                                        )
                                    }
                                >
                                    {serviceSourceOptions.map(opt => (
                                        <option
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {serviceSource !== 'manual' && (
                                <div className="mb-4" ref={serviceContainerRef}>
                                    {selectedService ? (
                                        <div className="flex items-center gap-2 input input-bordered w-full pr-10 relative">
                                            <span className="truncate">
                                                {getSelectedServiceLabel()}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={clearSelectedService}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs btn-circle"
                                            >
                                                <MdClose className="text-lg" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="relative">
                                            <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                                            <input
                                                type="text"
                                                placeholder="Buscar..."
                                                className="input input-bordered w-full pl-10"
                                                value={serviceSearchTerm}
                                                onChange={e => {
                                                    setServiceSearchTerm(
                                                        e.target.value
                                                    )
                                                    setShowServiceDropdown(true)
                                                }}
                                                onFocus={() => {
                                                    setShowServiceDropdown(true)
                                                    if (
                                                        serviceResults.length ===
                                                        0
                                                    )
                                                        searchServices('')
                                                }}
                                                autoComplete="off"
                                            />
                                            {showServiceDropdown && (
                                                <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                    {serviceLoading ? (
                                                        <div className="p-4 text-center">
                                                            <span className="loading loading-spinner loading-sm"></span>
                                                        </div>
                                                    ) : serviceResults.length ===
                                                      0 ? (
                                                        <div className="p-4 text-center text-base-content/60 text-sm">
                                                            Nenhum resultado
                                                            encontrado
                                                        </div>
                                                    ) : (
                                                        serviceResults.map(
                                                            item => {
                                                                const {
                                                                    title,
                                                                    subtitle,
                                                                } =
                                                                    getServiceResultLabel(
                                                                        item
                                                                    )
                                                                return (
                                                                    <button
                                                                        key={`${item._type}-${item.id}`}
                                                                        type="button"
                                                                        className="w-full px-4 py-3 text-left hover:bg-base-200 border-b border-base-200 last:border-b-0"
                                                                        onClick={() =>
                                                                            handleServiceSelect(
                                                                                item
                                                                            )
                                                                        }
                                                                    >
                                                                        <p className="font-medium truncate text-sm">
                                                                            {
                                                                                title
                                                                            }
                                                                        </p>
                                                                        <p className="text-xs text-base-content/60 truncate">
                                                                            {
                                                                                subtitle
                                                                            }
                                                                        </p>
                                                                    </button>
                                                                )
                                                            }
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">
                                            Valor dos Serviços *
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered"
                                        value={valorDisplay}
                                        onChange={e =>
                                            handleValorChange(e.target.value)
                                        }
                                        placeholder="R$ 0,00"
                                    />
                                </div>
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">
                                            Item Lista Serviço *
                                        </span>
                                    </label>
                                    <SearchableSelect
                                        options={SERVICE_LIST_ITEMS.map(
                                            item => ({
                                                value: item.code,
                                                label: item.label,
                                                group: item.group,
                                            })
                                        )}
                                        value={formData.itemListaServico}
                                        onChange={v =>
                                            update('itemListaServico', v)
                                        }
                                        placeholder="Buscar item de serviço..."
                                    />
                                </div>
                            </div>

                            <div className="form-control mt-4">
                                <label className="label">
                                    <span className="label-text">
                                        Discriminação do Serviço *
                                    </span>
                                </label>
                                <textarea
                                    className="textarea textarea-bordered h-24"
                                    value={formData.discriminacao}
                                    onChange={e =>
                                        update('discriminacao', e.target.value)
                                    }
                                    placeholder="Descreva o serviço prestado"
                                />
                            </div>

                            <div className="form-control mt-4">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-secondary"
                                        checked={formData.issRetido}
                                        onChange={e =>
                                            update(
                                                'issRetido',
                                                e.target.checked
                                            )
                                        }
                                    />
                                    <span className="label-text">
                                        ISS Retido
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <button
                            className="btn btn-ghost"
                            onClick={() => navigate('/fiscal/invoices')}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading && (
                                <span className="loading loading-spinner loading-sm"></span>
                            )}
                            Emitir NFS-e
                        </button>
                    </div>
                </>
            )}
        </div>
    )
}

export default NfseFormPage
